import { useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { PDF_PAGE_WIDTH } from '../../constants';
import PDFField from '../PDFField';
import styles from './styles.module.scss';
import { PDFDocument as PDFDoc } from 'pdf-lib';

type PDFDocumentProps = {
  pdf: PDF.PDFDocument;
  pdfDoc: PDFDoc | undefined;
  onFieldChange?: (key: string, value: any) => void;
  enableSignAll?: boolean;
  focusFirstRequiredField?: boolean;
  onFieldFocused?: () => void;
};

const PDFDocument = (props: PDFDocumentProps) => {
  const {
    pdf,
    pdfDoc,
    onFieldChange = () => {},
    enableSignAll = false,
    focusFirstRequiredField = false,
    onFieldFocused = () => {}
  } = props;
  const [numPages, setNumPages] = useState(0);

  // map of required fields to their elements
  const [requiredFieldMap, setRequiredFieldMap] = useState<{
    [key: string]: HTMLElement;
  }>({});

  const mapRequiredFieldElement = (el: HTMLElement, field: PDF.PDFField) => {
    if (el && el !== requiredFieldMap[field.key] && field.required) {
      setRequiredFieldMap((requiredFieldMap) => {
        return { ...requiredFieldMap, [field.key]: el };
      });
    }
  };

  function offsetY(el: HTMLElement) {
    if (!el) return 0;
    var rect = el.getBoundingClientRect(),
      scrollTop = window.scrollY || document.documentElement.scrollTop;
    return rect.top + scrollTop;
  }

  const sortByPosition = (a: any, b: any) => {
    // Sort primarily by the vertical position (y)
    if (a.position.y !== b.position.y) {
      return a.position.y - b.position.y;
    }
    // If vertical positions are the same, sort by the horizontal position (x)
    return a.position.x - b.position.x;
  };

  useEffect(() => {
    if (focusFirstRequiredField) {
      const pageNumber = pdfDoc?.getPageCount();
      if (!pageNumber) return;
      const unfilledRequiredFields = pdf.fields.filter(
        (field: PDF.PDFField) =>
          field.required &&
          field.type !== 'image' &&
          !(field.type === 'date' && field.linkedSignatureField) &&
          field.value === undefined &&
          field.page < pageNumber
      );
      // find the first one vertically
      const firstUnfilledRequiredField = unfilledRequiredFields.reduce(
        (first, field) => {
          const el = requiredFieldMap[field.key];
          if (!first) return { el, y: offsetY(el) };
          const y = offsetY(el);
          if (y < first.y) return { el, y };
          return first;
        },
        null
      );

      // scroll to the first unfilled required field at top of the viewport
      if (firstUnfilledRequiredField) {
        const elementPosition =
          firstUnfilledRequiredField.el.getBoundingClientRect().top +
          window.scrollY;
        const offsetPosition = elementPosition - 60;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        firstUnfilledRequiredField.el.focus?.();
        onFieldFocused();
      }
    }
  }, [
    pdf.fields,
    requiredFieldMap,
    focusFirstRequiredField,
    onFieldFocused,
    pdfDoc
  ]);

  return (
    <Document
      className={styles.pdfDocument}
      file={pdf.file}
      onLoadSuccess={(d: any) => {
        setNumPages(d._pdfInfo.numPages);
      }}
    >
      {new Array(numPages).fill(0).map((_, index) => (
        <Page
          className={styles.pdfPage}
          key={`page_${index}`}
          width={PDF_PAGE_WIDTH}
          pageNumber={index + 1}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        >
          {pdf.fields
            .filter((f: any) => f.page === index)
            .sort(sortByPosition)
            .map((field: any) => {
              const signAll =
                enableSignAll && field.type === 'signature'
                  ? (file: File) => {
                      const signType =
                        field.properties?.sign_type ?? 'signature';
                      pdf.fields
                        .filter(
                          (f: PDF.PDFField) =>
                            f.type === 'signature' &&
                            (f.properties?.sign_type ?? 'signature') ===
                              signType &&
                            !f.value
                        )
                        .forEach((f: PDF.PDFField) =>
                          onFieldChange(f.key, file)
                        );
                    }
                  : undefined;
              return (
                <PDFField
                  key={field.key}
                  ref={(el: HTMLElement) => mapRequiredFieldElement(el, field)}
                  field={field}
                  onComplete={(value: any) => onFieldChange(field.key, value)}
                  onSignAll={signAll}
                />
              );
            })}
        </Page>
      ))}
    </Document>
  );
};

export default PDFDocument;
